import type { QueryClient } from "@tanstack/react-query"
import { getQueryKey } from "@trpc/react-query"

import { api, type RouterOutputs } from "~/utils/api"
import { runPartialRefresh } from "~/utils/cacheUtils/runPartialRefresh"
import { MAX_PRODUCT_SETTING_ROWS } from "~/utils/suggestedQuantity"

type ApiUtils = ReturnType<typeof api.useUtils>
type InvalidateOptions = { cancelRefetch?: boolean }
type ListLine = RouterOutputs["allocation"]["listReplenishmentLines"]["lines"][number]

/** Every query that reads AllocationRateOfSale, for when the table was rebuilt. */
export function invalidateRateOfSaleReaders(utils: ApiUtils, options?: InvalidateOptions) {
  const allocation = utils.allocation
  void allocation.getRateOfSaleStatus.invalidate(undefined, {}, options)
  void allocation.getRuleImpactLines.invalidate(undefined, {}, options)
  void allocation.listReplenishmentLines.invalidate(undefined, {}, options)
  void allocation.getAllocationSalesMap.invalidate(undefined, {}, options)
  void allocation.getSalesWindowEnds.invalidate(undefined, {}, options)
  void allocation.countReplenishmentLinesWithTotal.invalidate(undefined, {}, options)
  void allocation.getStoreReport.invalidate(undefined, {}, options)
  void allocation.getProductReport.invalidate(undefined, {}, options)
  void allocation.getProductReportTotal.invalidate(undefined, {}, options)
  void allocation.getProductReportContext.invalidate(undefined, {}, options)
  void allocation.getProductStoreReport.invalidate(undefined, {}, options)
  void allocation.getProductLineReport.invalidate(undefined, {}, options)
  void allocation.getReferenceRosCoverage.invalidate(undefined, {}, options)
  void allocation.previewReferenceRos.invalidate(undefined, {}, options)
}

/** Counts and reports sum every line, so no single line can be patched into them. */
function lineAggregates(utils: ApiUtils) {
  const allocation = utils.allocation
  return [
    allocation.countReplenishmentLinesWithTotal,
    allocation.getStoreReport,
    allocation.getProductReport,
    allocation.getProductReportTotal,
    allocation.getProductStoreReport,
    allocation.getProductLineReport,
  ]
}

/** Every query that reads AllocationPlanLine, for when a run rewrote the lines. */
export function invalidateAllLineReaders(utils: ApiUtils, options?: InvalidateOptions) {
  const allocation = utils.allocation
  void allocation.getRuleImpactLines.invalidate(undefined, {}, options)
  void allocation.getLineRunDetails.invalidate(undefined, {}, options)
  void allocation.listReplenishmentLines.invalidate(undefined, {}, options)
  void allocation.getStoreDetail.invalidate(undefined, {}, options)
  void allocation.getWarehouseSkuFinalTotal.invalidate(undefined, {}, options)
  void allocation.getProductSettings.invalidate(undefined, {}, options)
  void allocation.getProductSettingsChangelog.invalidate(undefined, {}, options)
  void allocation.getStoreTimeline.invalidate(undefined, {}, options)
  for (const aggregate of lineAggregates(utils)) void aggregate.invalidate(undefined, {}, options)
}

/** The reads a line write changes besides the grid's own list. */
export function invalidateLineDependents(utils: ApiUtils, options?: InvalidateOptions) {
  const allocation = utils.allocation
  void allocation.getRuleImpactLines.invalidate(undefined, { refetchType: "none" }, options)
  void allocation.getStoreDetail.invalidate(undefined, {}, options)
  void allocation.getWarehouseSkuFinalTotal.invalidate(undefined, {}, options)
}

/**
 * The reads a line settings save changes besides the grid's own rows. Counts and reports only
 * go stale and re-read when next shown.
 */
export function invalidateLineSettingsReaders(
  utils: ApiUtils,
  lineIds: readonly string[],
  options?: InvalidateOptions,
) {
  const allocation = utils.allocation
  const ids = new Set(lineIds)
  invalidateLineDependents(utils, options)
  void allocation.getLineRunDetails.invalidate(
    undefined,
    {
      predicate: (query) => {
        const key = query.queryKey[1] as { input?: { lineId?: string } } | undefined
        return key?.input?.lineId !== undefined && ids.has(key.input.lineId)
      },
    },
    options,
  )
  void allocation.getProductSettings.invalidate(undefined, {}, options)
  void allocation.getProductSettingsChangelog.invalidate(undefined, {}, options)
  void allocation.getStoreTimeline.invalidate(undefined, {}, options)
  for (const aggregate of lineAggregates(utils)) {
    void aggregate.invalidate(undefined, { refetchType: "none" }, options)
  }
}

/**
 * Merges these lines' rows into every cached line list. Lines a list doesn't hold stay out, and a
 * row that no longer matches a list's filters stays until that list next refetches. A list
 * mid-fetch may have read the rows before the save, so it is cancelled and read again; that also
 * covers a list still loading its first page, which holds no rows to patch.
 */
export function patchListedLines(
  utils: ApiUtils,
  queryClient: QueryClient,
  fresh: readonly (Pick<ListLine, "resultLineId"> & Partial<ListLine>)[],
) {
  if (fresh.length === 0) return
  const freshById = new Map(fresh.map((line) => [line.resultLineId, line]))
  const listKey = getQueryKey(api.allocation.listReplenishmentLines)
  for (const { queryKey } of queryClient.getQueryCache().findAll({
    queryKey: listKey,
    predicate: (query) => query.state.fetchStatus === "fetching",
  })) {
    void queryClient
      .cancelQueries({ queryKey, exact: true })
      .then(() => queryClient.refetchQueries({ queryKey, exact: true }))
  }
  utils.allocation.listReplenishmentLines.setQueriesData({}, {}, (old) =>
    old
      ? {
          ...old,
          lines: old.lines.map((line) => {
            const patch = freshById.get(line.resultLineId)
            return patch ? { ...line, ...patch } : line
          }),
        }
      : old,
  )
}

/** Another planner saved these lines' settings: re-reads only those rows into the open grids. */
export async function refreshAllocationLines(
  utils: ApiUtils,
  queryClient: QueryClient,
  lineIds: string[],
  options?: InvalidateOptions,
) {
  if (lineIds.length > MAX_PRODUCT_SETTING_ROWS) return invalidateAllLineReaders(utils, options)
  invalidateLineSettingsReaders(utils, lineIds, options)
  await runPartialRefresh({
    cancel: () => {},
    fetch: () =>
      utils.allocation.getReplenishmentLinesById.fetch({ lineIds }, { staleTime: 0, gcTime: 0 }),
    apply: (fresh) => patchListedLines(utils, queryClient, fresh),
    fallback: () => void utils.allocation.listReplenishmentLines.invalidate(undefined, {}, options),
  })
}
